import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { UnidadAutocomplete } from "@/components/UnidadAutocomplete";
import { COST_TYPE_LABELS } from "@/components/LoadBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, XCircle, Plus, Trash2, ArrowLeft, Gauge } from "lucide-react";

const TRIP_OPTIONS = [
  { value: "nacional_mx", label: "Nacional MX", company: "Trébol (T)" },
  { value: "nacional_us", label: "Nacional US", company: "Álvarez Trucking (AL)" },
  { value: "transfronterizo", label: "Transfronterizo", company: "Trébol (T) + Álvarez Trucking (AL)" },
];

const newCost = (defaultMoneda = "USD") => ({
  tipo_costo: "otro", carrier_id: null, carrier_label: "", concepto: "", monto: "", moneda: defaultMoneda, estado_pago: "pendiente",
});
export { newCost };

const emptyLeg = (moneda) => ({ carrier_id: "", carrier_label: "", monto_cobro: "", moneda_cobro: moneda, costos: [] });

export default function LoadForm() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    tipo_viaje: "nacional_mx",
    client_id: "", client_label: "",
    referencia_cliente: "",
    origen_ciudad: "", origen_estado: "", origen_pais: "MX",
    destino_ciudad: "", destino_estado: "", destino_pais: "US",
    fecha_pickup: "", fecha_delivery: "",
    notas: "", unidad: "", numero_factura: "", operador: "", placas: "",
    carrier_id: "", carrier_label: "",
    monto_cobro: "", moneda_cobro: "USD",
    costos: [],
    tramo_mx: emptyLeg("MXN"),
    tramo_us: emptyLeg("USD"),
  });

  const [dup, setDup] = useState(null); // {exacto, difuso}
  const [confirmDifuso, setConfirmDifuso] = useState(false);
  const [confirmReferenciaRepetida, setConfirmReferenciaRepetida] = useState(false);
  const [confirmCobroDoble, setConfirmCobroDoble] = useState(false);
  const [tarifa, setTarifa] = useState(null);
  const [confirmFueraTarifa, setConfirmFueraTarifa] = useState(false);
  const [fueraTarifaServerDetail, setFueraTarifaServerDetail] = useState(null);
  const [tipoCambio, setTipoCambio] = useState(17.5);
  const [unidadTipo, setUnidadTipo] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setTipoCambio(data.tipo_cambio_usd_mxn || 17.5)).catch(() => {});
  }, []);

  const isXB = form.tipo_viaje === "transfronterizo";

  const fetchTarifa = async (overrides = {}) => {
    const origen = overrides.origen_ciudad ?? form.origen_ciudad;
    const destino = overrides.destino_ciudad ?? form.destino_ciudad;
    const clientId = overrides.client_id ?? form.client_id;
    const tipoUnidad = form.tipo_viaje === "nacional_mx" ? (overrides.tipo_unidad ?? unidadTipo) : null;
    if (!origen || !destino) { setTarifa(null); return; }
    try {
      const { data } = await api.get("/rate-cards/match", {
        params: { origen_ciudad: origen, destino_ciudad: destino, client_id: clientId || "", tipo_unidad: tipoUnidad || "" },
      });
      setTarifa(data);
      setConfirmFueraTarifa(false);
    } catch {
      setTarifa(null);
    }
  };

  // Nacional MX: resuelve el tipo de la unidad (catálogo Unidades) para que el matching de
  // tarifario pueda usar origen+destino+tipo_unidad, no solo origen+destino.
  useEffect(() => {
    if (form.tipo_viaje !== "nacional_mx" || !form.unidad) { setUnidadTipo(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/unidades", { params: { search: form.unidad, activo: "true", limit: 5 } });
        const exact = data.items.find((u) => u.economico.toLowerCase() === form.unidad.trim().toLowerCase());
        setUnidadTipo(exact ? exact.tipo : null);
      } catch { setUnidadTipo(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.unidad, form.tipo_viaje]);

  useEffect(() => {
    if (form.origen_ciudad && form.destino_ciudad) fetchTarifa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidadTipo]);

  const toUsd = (monto, moneda) => (moneda === "MXN" ? (parseFloat(monto || 0) / tipoCambio) : parseFloat(monto || 0));

  const legTarifaCheck = (costos, montoCobro, monedaCobro) => {
    if (!tarifa) return null;
    const costosUsd = costos.reduce((s, c) => s + toUsd(c.monto, c.moneda), 0);
    const techoUsd = toUsd(tarifa.costo_techo, tarifa.moneda);
    const montoCobroUsd = toUsd(montoCobro, monedaCobro);
    const margenResultante = montoCobroUsd ? Math.round(((montoCobroUsd - costosUsd) / montoCobroUsd) * 10000) / 100 : 0;
    return { excede: costosUsd > techoUsd, costosUsd, techoUsd, margenResultante, margenObjetivo: tarifa.margen_objetivo_pct };
  };

  const tarifaCheckMain = !isXB ? legTarifaCheck(form.costos, form.monto_cobro, form.moneda_cobro) : null;
  // En transfronterizo, el techo se evalúa contra la SUMA consolidada de ambos tramos (MX+US),
  // igual que en el backend (_tarifa_check_consolidado / _tarifa_check_leg) — no por tramo.
  const tarifaCheckXB = isXB && tarifa ? (() => {
    const costosMxUsd = form.tramo_mx.costos.reduce((s, c) => s + toUsd(c.monto, c.moneda), 0);
    const costosUsUsd = form.tramo_us.costos.reduce((s, c) => s + toUsd(c.monto, c.moneda), 0);
    const costosTotalUsd = costosMxUsd + costosUsUsd;
    const techoUsd = toUsd(tarifa.costo_techo, tarifa.moneda);
    const montoMxUsd = toUsd(form.tramo_mx.monto_cobro, form.tramo_mx.moneda_cobro);
    const montoUsUsd = toUsd(form.tramo_us.monto_cobro, form.tramo_us.moneda_cobro);
    const montoTotalUsd = montoMxUsd + montoUsUsd;
    const margenResultante = montoTotalUsd ? Math.round(((montoTotalUsd - costosTotalUsd) / montoTotalUsd) * 10000) / 100 : 0;
    return { excede: costosTotalUsd > techoUsd, costosMxUsd, costosUsUsd, costosTotalUsd, techoUsd, margenResultante, margenObjetivo: tarifa.margen_objetivo_pct };
  })() : null;
  const excedeTecho = !!(tarifaCheckMain?.excede || tarifaCheckXB?.excede || fueraTarifaServerDetail);
  const peorMargen = tarifaCheckMain?.excede ? tarifaCheckMain : tarifaCheckXB;

  const checkDuplicate = async () => {
    if (!form.client_id || !form.referencia_cliente || !form.origen_ciudad || !form.destino_ciudad || !form.fecha_pickup) return;
    try {
      const { data } = await api.get("/loads/check-duplicate", {
        params: {
          client_id: form.client_id, referencia_cliente: form.referencia_cliente,
          origen_ciudad: form.origen_ciudad, destino_ciudad: form.destino_ciudad, fecha_pickup: form.fecha_pickup,
        },
      });
      setDup(data.exacto || data.difuso ? data : null);
      setConfirmDifuso(false);
      setConfirmReferenciaRepetida(false);
    } catch {
      /* silent — final validation happens server side on submit */
    }
  };
  const cobroDobleDetectado = isXB && Number(form.tramo_mx.monto_cobro || 0) > 0 && Number(form.tramo_us.monto_cobro || 0) > 0;

  const buildPayload = () => {
    const cost = (c) => ({
      tipo_costo: c.tipo_costo, carrier_id: c.carrier_id || null, concepto: c.concepto || "",
      monto: parseFloat(c.monto || 0), moneda: c.moneda, estado_pago: c.estado_pago,
    });
    const base = {
      tipo_viaje: form.tipo_viaje, client_id: form.client_id, referencia_cliente: form.referencia_cliente,
      origen_ciudad: form.origen_ciudad, origen_estado: form.origen_estado, origen_pais: form.origen_pais,
      destino_ciudad: form.destino_ciudad, destino_estado: form.destino_estado, destino_pais: form.destino_pais,
      fecha_pickup: form.fecha_pickup, fecha_delivery: form.fecha_delivery, notas: form.notas,
      unidad: form.unidad, numero_factura: form.numero_factura, operador: form.operador, placas: form.placas,
      confirmar_duplicado_difuso: confirmDifuso, confirmar_cobro_doble: confirmCobroDoble,
      confirmar_fuera_tarifa: confirmFueraTarifa, confirmar_referencia_repetida: confirmReferenciaRepetida,
    };
    if (isXB) {
      return {
        ...base,
        tramo_mx: { carrier_id: form.tramo_mx.carrier_id || null, monto_cobro: parseFloat(form.tramo_mx.monto_cobro || 0), moneda_cobro: form.tramo_mx.moneda_cobro, costos: form.tramo_mx.costos.map(cost) },
        tramo_us: { carrier_id: form.tramo_us.carrier_id || null, monto_cobro: parseFloat(form.tramo_us.monto_cobro || 0), moneda_cobro: form.tramo_us.moneda_cobro, costos: form.tramo_us.costos.map(cost) },
      };
    }
    return {
      ...base,
      carrier_id: form.carrier_id || null, monto_cobro: parseFloat(form.monto_cobro || 0), moneda_cobro: form.moneda_cobro,
      costos: form.costos.map(cost),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/loads", buildPayload());
      toast.success(`Load ${data.numero_load} creado`);
      navigate(`/loads/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "duplicado_exacto") {
        setDup({ exacto: detail, difuso: null });
        toast.error(detail.message);
      } else if (detail?.code === "posible_duplicado") {
        setDup({ exacto: null, difuso: detail });
        toast.warning(detail.message);
      } else if (detail?.code === "cobro_doble") {
        toast.warning(detail.message);
      } else if (detail?.code === "fuera_de_tarifa") {
        setFueraTarifaServerDetail(detail);
        toast.warning(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const requiredOk = form.client_id && form.referencia_cliente && form.origen_ciudad && form.destino_ciudad
    && form.fecha_pickup && form.fecha_delivery;
  const needsTarifaConfirm = excedeTecho && !confirmFueraTarifa;
  const needsExactoConfirm = !!dup?.exacto && !confirmReferenciaRepetida;
  const needsDifusoConfirm = !!dup?.difuso && !confirmDifuso;
  const needsCobroDobleConfirm = cobroDobleDetectado && !confirmCobroDoble;
  const canSave = requiredOk && !needsExactoConfirm && !needsDifusoConfirm && !needsCobroDobleConfirm && !needsTarifaConfirm && !saving;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Nuevo load"
        subtitle="El número se genera automáticamente al guardar y nunca podrá editarse"
        actions={
          <Button variant="outline" onClick={() => navigate("/loads")} className="gap-2" data-testid="load-form-back">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
        }
      />

      {/* Identificación */}
      <Card className="mb-5">
        <CardHeader><CardTitle className="font-heading text-base">1. Identificación</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Tipo de viaje</Label>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {TRIP_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    const defaults = opt.value === "nacional_mx"
                      ? { origen_pais: "MX", destino_pais: "MX" }
                      : opt.value === "nacional_us"
                        ? { origen_pais: "US", destino_pais: "US" }
                        : { origen_pais: "MX", destino_pais: "US" };
                    setForm({ ...form, tipo_viaje: opt.value, ...defaults });
                  }}
                  data-testid={`load-tipo-viaje-${opt.value}`}
                  className={`rounded-md border p-3 text-left text-sm transition-colors ${
                    form.tipo_viaje === opt.value ? "border-primary bg-green-50" : "border-border hover:bg-slate-50"
                  }`}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs text-muted-foreground">{opt.company}</div>
                </button>
              ))}
            </div>
            {form.tipo_viaje === "nacional_mx" && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="load-tn-series-hint">
                El número de este load saldrá de la serie <span className="font-mono font-semibold">TN-#####</span> (nacional MX, flota propia) — nunca de la serie T-#### institucional de transfronterizos.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Cliente *</Label>
              <div className="mt-1">
                <EntityAutocomplete
                  type="clients" value={form.client_id} label={form.client_label}
                  onSelect={(c) => { setForm({ ...form, client_id: c.id, client_label: `${c.codigo} — ${c.razon_social}` }); fetchTarifa({ client_id: c.id }); }}
                  testId="load-client-autocomplete"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Referencia del cliente *</Label>
              <Input
                className="mt-1" value={form.referencia_cliente}
                onChange={(e) => setForm({ ...form, referencia_cliente: e.target.value })}
                onBlur={checkDuplicate} data-testid="load-referencia-cliente"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <Label className="text-xs text-muted-foreground">Unidad (placa/identificación)</Label>
              <div className="mt-1">
                <UnidadAutocomplete
                  value={form.unidad}
                  onChange={(v) => setForm({ ...form, unidad: v })}
                  testId="load-unidad"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Número de factura</Label>
              <Input
                className="mt-1" value={form.numero_factura}
                onChange={(e) => setForm({ ...form, numero_factura: e.target.value })}
                data-testid="load-numero-factura"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Operador</Label>
              <Input
                className="mt-1" value={form.operador}
                onChange={(e) => setForm({ ...form, operador: e.target.value })}
                data-testid="load-operador"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Placas del camión</Label>
              <Input
                className="mt-1" value={form.placas} placeholder="87-AD-5Z"
                onChange={(e) => setForm({ ...form, placas: e.target.value })}
                data-testid="load-placas"
              />
            </div>
          </div>

          {dup?.exacto && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3" data-testid="load-exact-duplicate-banner">
              <div className="flex items-center gap-2 font-medium text-red-800">
                <XCircle className="h-4 w-4" /> Referencia repetida para este cliente
              </div>
              <p className="mt-1 text-sm text-red-800">{dup.exacto.message}</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate(`/loads/${dup.exacto.load_id}`)} data-testid="load-exact-duplicate-link">
                Abrir {dup.exacto.numero_load}
              </Button>
              <label className="mt-2 flex items-center gap-2 text-sm text-red-800">
                <input type="checkbox" checked={confirmReferenciaRepetida} onChange={(e) => setConfirmReferenciaRepetida(e.target.checked)} data-testid="load-confirm-referencia-repetida-checkbox" />
                Confirmo que es un movimiento distinto con la misma referencia (ej. TONU y flete, rescate y viaje original) — se creará con número de viaje propio
              </label>
            </div>
          )}
          {!dup?.exacto && dup?.difuso && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="load-fuzzy-duplicate-banner">
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Posible duplicado
              </div>
              <p className="mt-1 text-sm text-amber-800">{dup.difuso.message} (load {dup.difuso.numero_load})</p>
              <label className="mt-2 flex items-center gap-2 text-sm text-amber-800">
                <input type="checkbox" checked={confirmDifuso} onChange={(e) => setConfirmDifuso(e.target.checked)} data-testid="load-confirm-difuso-checkbox" />
                Confirmo que es un viaje distinto
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ruta */}
      <Card className="mb-5">
        <CardHeader><CardTitle className="font-heading text-base">2. Ruta</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="grid grid-cols-3 gap-2 sm:col-span-2">
            <div className="col-span-1">
              <Label className="text-xs text-muted-foreground">Origen ciudad *</Label>
              <Input className="mt-1" value={form.origen_ciudad} onChange={(e) => setForm({ ...form, origen_ciudad: e.target.value })} onBlur={() => { checkDuplicate(); fetchTarifa(); }} data-testid="load-origen-ciudad" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <Input className="mt-1" value={form.origen_estado} onChange={(e) => setForm({ ...form, origen_estado: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">País</Label>
              <Select value={form.origen_pais} onValueChange={(v) => setForm({ ...form, origen_pais: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MX">MX</SelectItem><SelectItem value="US">US</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:col-span-2">
            <div className="col-span-1">
              <Label className="text-xs text-muted-foreground">Destino ciudad *</Label>
              <Input className="mt-1" value={form.destino_ciudad} onChange={(e) => setForm({ ...form, destino_ciudad: e.target.value })} onBlur={() => { checkDuplicate(); fetchTarifa(); }} data-testid="load-destino-ciudad" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <Input className="mt-1" value={form.destino_estado} onChange={(e) => setForm({ ...form, destino_estado: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">País</Label>
              <Select value={form.destino_pais} onValueChange={(v) => setForm({ ...form, destino_pais: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MX">MX</SelectItem><SelectItem value="US">US</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Fecha pickup *</Label>
            <Input type="date" className="mt-1" value={form.fecha_pickup} onChange={(e) => setForm({ ...form, fecha_pickup: e.target.value })} onBlur={checkDuplicate} data-testid="load-fecha-pickup" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Fecha delivery *</Label>
            <Input type="date" className="mt-1" value={form.fecha_delivery} onChange={(e) => setForm({ ...form, fecha_delivery: e.target.value })} data-testid="load-fecha-delivery" />
          </div>
        </CardContent>
      </Card>

      {/* Tarifario sugerido */}
      {tarifa && (
        <Card className="mb-5" data-testid="load-tarifa-panel">
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base"><Gauge className="h-4 w-4 text-primary" /> Tarifa sugerida para esta ruta</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Tarifa de referencia</p>
                <p className="font-mono font-semibold" data-testid="load-tarifa-referencia">{tarifa.tarifa_referencia} {tarifa.moneda}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Costo techo</p>
                <p className="font-mono font-semibold" data-testid="load-tarifa-techo">{tarifa.costo_techo} {tarifa.moneda}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Margen objetivo</p>
                <p className="font-mono font-semibold">{tarifa.margen_objetivo_pct}%</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Aplica a: {tarifa.client_nombre}</p>
            {excedeTecho && (
              <div className="rounded-md border border-orange-300 bg-orange-50 p-3" data-testid="load-fuera-tarifa-banner">
                <div className="flex items-center gap-2 font-medium text-orange-800">
                  <AlertTriangle className="h-4 w-4" />
                  {isXB ? "Este viaje transfronterizo supera el techo de costo de la ruta (suma de ambos tramos)." : "Este viaje supera el techo de costo de la ruta."}
                </div>
                {fueraTarifaServerDetail ? (
                  <p className="mt-1 text-sm text-orange-800">{fueraTarifaServerDetail.message}</p>
                ) : isXB && tarifaCheckXB ? (
                  <p className="mt-1 text-sm text-orange-800">
                    Costo tramo MX: ${tarifaCheckXB.costosMxUsd.toFixed(2)} USD. Costo tramo US: ${tarifaCheckXB.costosUsUsd.toFixed(2)} USD.
                    Total: ${tarifaCheckXB.costosTotalUsd.toFixed(2)} USD vs. techo ${tarifaCheckXB.techoUsd.toFixed(2)} USD.
                    Margen consolidado resultante: {tarifaCheckXB.margenResultante}% vs. objetivo {tarifaCheckXB.margenObjetivo}%.
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-orange-800">
                    Margen resultante: {peorMargen?.margenResultante}%. Margen objetivo: {peorMargen?.margenObjetivo}%.
                  </p>
                )}
                <label className="mt-2 flex items-center gap-2 text-sm text-orange-800">
                  <input type="checkbox" checked={confirmFueraTarifa} onChange={(e) => setConfirmFueraTarifa(e.target.checked)} data-testid="load-confirm-fuera-tarifa-checkbox" />
                  Confirmo y apruebo continuar fuera de tarifa
                </label>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cobro + Costos */}
      {!isXB ? (
        <Card className="mb-5">
          <CardHeader><CardTitle className="font-heading text-base">3. Cobro al cliente y 4. Costos</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Carrier</Label>
                <div className="mt-1">
                  <EntityAutocomplete type="carriers" value={form.carrier_id} label={form.carrier_label}
                    onSelect={(c) => setForm({ ...form, carrier_id: c.id, carrier_label: `${c.codigo} — ${c.razon_social}` })}
                    testId="load-carrier-autocomplete" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Monto cobro</Label>
                <Input type="number" className="mt-1" value={form.monto_cobro} onChange={(e) => setForm({ ...form, monto_cobro: e.target.value })} data-testid="load-monto-cobro" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Moneda</Label>
                <Select value={form.moneda_cobro} onValueChange={(v) => setForm({ ...form, moneda_cobro: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <CostosEditor costos={form.costos} setCostos={(c) => setForm({ ...form, costos: c })} testPrefix="load"
              defaultMoneda={form.tipo_viaje === "nacional_mx" ? "MXN" : "USD"} />
          </CardContent>
        </Card>
      ) : (
        <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Tramo México (T)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Carrier MX</Label>
                <div className="mt-1">
                  <EntityAutocomplete type="carriers" paisFilter="MX" value={form.tramo_mx.carrier_id} label={form.tramo_mx.carrier_label}
                    onSelect={(c) => setForm({ ...form, tramo_mx: { ...form.tramo_mx, carrier_id: c.id, carrier_label: `${c.codigo} — ${c.razon_social}` } })}
                    testId="load-tramo-mx-carrier" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Monto cobro</Label>
                  <Input type="number" className="mt-1" value={form.tramo_mx.monto_cobro} onChange={(e) => setForm({ ...form, tramo_mx: { ...form.tramo_mx, monto_cobro: e.target.value } })} data-testid="load-tramo-mx-monto" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Moneda</Label>
                  <Select value={form.tramo_mx.moneda_cobro} onValueChange={(v) => setForm({ ...form, tramo_mx: { ...form.tramo_mx, moneda_cobro: v } })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <CostosEditor costos={form.tramo_mx.costos} setCostos={(c) => setForm({ ...form, tramo_mx: { ...form.tramo_mx, costos: c } })} testPrefix="load-mx" defaultMoneda="MXN" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Tramo Estados Unidos (AL)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Carrier US</Label>
                <div className="mt-1">
                  <EntityAutocomplete type="carriers" paisFilter="US" value={form.tramo_us.carrier_id} label={form.tramo_us.carrier_label}
                    onSelect={(c) => setForm({ ...form, tramo_us: { ...form.tramo_us, carrier_id: c.id, carrier_label: `${c.codigo} — ${c.razon_social}` } })}
                    testId="load-tramo-us-carrier" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Monto cobro</Label>
                  <Input type="number" className="mt-1" value={form.tramo_us.monto_cobro} onChange={(e) => setForm({ ...form, tramo_us: { ...form.tramo_us, monto_cobro: e.target.value } })} data-testid="load-tramo-us-monto" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Moneda</Label>
                  <Select value={form.tramo_us.moneda_cobro} onValueChange={(v) => setForm({ ...form, tramo_us: { ...form.tramo_us, moneda_cobro: v } })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <CostosEditor costos={form.tramo_us.costos} setCostos={(c) => setForm({ ...form, tramo_us: { ...form.tramo_us, costos: c } })} testPrefix="load-us" defaultMoneda="USD" />
            </CardContent>
          </Card>
        </div>
      )}

      {cobroDobleDetectado && (
        <div className="mb-5 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="load-cobro-doble-banner">
          <div className="flex items-center gap-2 font-medium text-amber-800">
            <AlertTriangle className="h-4 w-4" /> Posible doble facturación
          </div>
          <p className="mt-1 text-sm text-amber-800">Ambos tramos tienen un cobro registrado al mismo cliente. Verifica que no sea el mismo concepto facturado dos veces.</p>
          <label className="mt-2 flex items-center gap-2 text-sm text-amber-800">
            <input type="checkbox" checked={confirmCobroDoble} onChange={(e) => setConfirmCobroDoble(e.target.checked)} data-testid="load-confirm-cobro-doble-checkbox" />
            Confirmo que ambos cobros son correctos y distintos
          </label>
        </div>
      )}

      {/* Notas */}
      <Card className="mb-5">
        <CardHeader><CardTitle className="font-heading text-base">5. Notas</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="load-notas" />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 pb-10">
        <Button variant="outline" onClick={() => navigate("/loads")}>Cancelar</Button>
        <Button onClick={save} disabled={!canSave} data-testid="load-save-btn">
          {saving ? "Guardando..." : "Guardar load"}
        </Button>
      </div>
    </div>
  );
}

export function CostosEditor({ costos, setCostos, testPrefix, defaultMoneda = "USD" }) {
  const addRow = () => setCostos([...costos, newCost(defaultMoneda)]);
  const removeRow = (i) => setCostos(costos.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => setCostos(costos.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">Costos</Label>
        <Button type="button" size="sm" variant="outline" onClick={addRow} className="gap-1" data-testid={`${testPrefix}-add-cost`}>
          <Plus className="h-3.5 w-3.5" /> Agregar costo
        </Button>
      </div>
      {costos.length === 0 && <p className="text-xs text-muted-foreground">Sin costos agregados.</p>}
      <div className="space-y-2">
        {costos.map((c, i) => (
          <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-md border border-border p-2" data-testid={`${testPrefix}-cost-row-${i}`}>
            <div className="col-span-3">
              <Label className="text-[10px] text-muted-foreground">Tipo</Label>
              <Select value={c.tipo_costo} onValueChange={(v) => updateRow(i, "tipo_costo", v)}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(COST_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(c.tipo_costo === "carrier_mx" || c.tipo_costo === "carrier_us") && (
              <div className="col-span-3">
                <Label className="text-[10px] text-muted-foreground">Carrier</Label>
                <div className="mt-1">
                  <EntityAutocomplete
                    type="carriers" paisFilter={c.tipo_costo === "carrier_mx" ? "MX" : "US"}
                    value={c.carrier_id} label={c.carrier_label}
                    onSelect={(car) => setCostos(costos.map((cc, idx) => (idx === i ? { ...cc, carrier_id: car.id, carrier_label: `${car.codigo} — ${car.razon_social}` } : cc)))}
                    testId={`${testPrefix}-cost-row-${i}-carrier`}
                  />
                </div>
              </div>
            )}
            {(c.tipo_costo === "otro" || c.tipo_costo === "diesel" || c.tipo_costo === "casetas"
              || c.tipo_costo === "maniobras" || c.tipo_costo === "viaticos_operador" || c.tipo_costo === "otros_operativos") && (
              <div className="col-span-3">
                <Label className="text-[10px] text-muted-foreground">Concepto</Label>
                <Input className="mt-1 h-9" value={c.concepto} onChange={(e) => updateRow(i, "concepto", e.target.value)} />
              </div>
            )}
            <div className={c.tipo_costo === "cruce" || c.tipo_costo === "demoras" || c.tipo_costo === "papeleria" ? "col-span-3" : "col-span-2"}>
              <Label className="text-[10px] text-muted-foreground">Monto</Label>
              <Input type="number" className="mt-1 h-9" value={c.monto} onChange={(e) => updateRow(i, "monto", e.target.value)} data-testid={`${testPrefix}-cost-row-${i}-monto`} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] text-muted-foreground">Moneda</Label>
              <Select value={c.moneda} onValueChange={(v) => updateRow(i, "moneda", v)}>
                <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-1 flex justify-end">
              <Button type="button" size="icon" variant="ghost" onClick={() => removeRow(i)} data-testid={`${testPrefix}-cost-row-${i}-remove`}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
